import * as React from "react";
import { ServiceItem } from "../../calcBusterTypes";
import { groupBy } from "shared/util/common/util";
import {
  Badge,
  Box,
  Pagination,
  SpaceBetween,
  Table,
  TextFilter,
} from "@amzn/awsui-components-react";
import { USDollar } from "../../util";

type ServiceData = {
  serviceCode: string;
  cost: number;
  percentage: string;
  count: number;
  paths: string[];
  description: string;
};

type SortingField = keyof ServiceData;

const columnDefinitions = [
  {
    id: "serviceCode",
    header: "Service Code",
    cell: (item: ServiceData) => item.serviceCode || "",
    sortingField: "serviceCode",
    isRowHeader: true,
  },
  {
    id: "description",
    header: "Description",
    cell: (item: ServiceData) => item.description || "N/A",
    sortingField: "description",
  },
  {
    id: "paths",
    header: "Modernization Paths",
    // cell: item => item.paths.join(', ') || 'N/A',
    cell: (item: ServiceData) => (
      <SpaceBetween direction="vertical" size="xs">
        {item.paths.length > 0 ? (
          item.paths?.map((path) => <Badge color="severity-low">{path}</Badge>)
        ) : (
          <Badge color="red">No Modernization</Badge>
        )}
      </SpaceBetween>
    ),
    sortingField: "paths",
  },
  {
    id: "cost",
    header: "Monthly Cost",
    cell: (item: ServiceData) => USDollar.format(item.cost || 0),
    sortingField: "cost",
  },
  {
    id: "percentage",
    header: "Percentage",
    cell: (item: ServiceData) => `${item.percentage || 0}%`,
    sortingField: "percentage",
  },
];

const ModernServicesTable: React.FC<{
  services: ServiceItem[];
}> = ({ services }) => {
  const DEFAULT_PREFERENCE = { pageSize: 10 };
  const [filteringText, setFilteringText] = React.useState("");
  const [currentPage, setCurrentPage] = React.useState(1);
  const [preferences, setPreferences] = React.useState(DEFAULT_PREFERENCE);
  const [sortingField, setSortingField] = React.useState<SortingField>("cost");
  const [sortingDescending, setSortingDescending] = React.useState(true);

  // Process data once and memoize it
  const data = React.useMemo(() => {
    // Filter only modern services

    const grouped = groupBy(services, "serviceCode");
    const totalCost = Object.values(grouped)
      .flat()
      .reduce((sum: number, s) => {
        const service = s as ServiceItem;
        return sum + service.serviceCost;
      }, 0);

    return Object.keys(grouped).map((key) => {
      const servicesInGroup = grouped[key];
      const value: number = servicesInGroup
        .map((s: ServiceItem) => s.serviceCost)
        .reduce((prev: number, curr: number) => prev + curr, 0);

      // Get all unique modernization paths for this service
      const paths: string[] = [
        ...new Set(
          servicesInGroup
            .flatMap((s: ServiceItem) => s.modernizationPaths || [])
            .filter(
              (path: string): path is string =>
                typeof path === "string" && path.length > 0
            )
        ),
      ] as string[];

      return {
        serviceCode: key,
        cost: value,
        percentage: ((value / totalCost) * 100).toFixed(1),
        count: servicesInGroup.length,
        paths: paths,
        description: servicesInGroup[0]?.description || "N/A",
      } as ServiceData;
    });
  }, [services]);

  // Filter items based on text input
  const filteredItems = React.useMemo(() => {
    if (!filteringText) return data;

    return data.filter(
      (item) =>
        item.serviceCode?.toLowerCase().includes(filteringText.toLowerCase()) ||
        item.paths.some((path) =>
          path.toLowerCase().includes(filteringText.toLowerCase())
        ) ||
        item.description.toLowerCase().includes(filteringText.toLowerCase())
    );
  }, [data, filteringText]);

  // Sort items based on sorting configuration
  const sortedItems = React.useMemo(() => {
    if (!filteredItems.length) return [];

    const sorted = [...filteredItems];

    if (!sortingField) return sorted;

    return sorted.sort((a: ServiceData, b: ServiceData) => {
      let valueA = a[sortingField];
      let valueB = b[sortingField];

      // Handle numeric values
      if (sortingField === "cost" || sortingField === "count") {
        valueA = Number(valueA) || 0;
        valueB = Number(valueB) || 0;
      }
      // Handle percentage
      else if (sortingField === "percentage") {
        valueA = parseFloat(valueA as string) || 0;
        valueB = parseFloat(valueB as string) || 0;
      }
      // Handle paths array
      else if (sortingField === "paths") {
        valueA = (valueA as string[]).join(", ").toLowerCase();
        valueB = (valueB as string[]).join(", ").toLowerCase();
      }
      // Handle strings
      else {
        valueA = String(valueA || "").toLowerCase();
        valueB = String(valueB || "").toLowerCase();
      }

      // Apply sorting direction
      if (valueA < valueB) return sortingDescending ? 1 : -1;
      if (valueA > valueB) return sortingDescending ? -1 : 1;
      return 0;
    });
  }, [filteredItems, sortingField, sortingDescending]);

  // Calculate pagination
  const ITEMS_PER_PAGE = preferences.pageSize;
  const pagesCount = Math.ceil(sortedItems.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, sortedItems.length);
  const paginatedItems = sortedItems.slice(startIndex, endIndex);

  return (
    <Table
      columnDefinitions={columnDefinitions}
      items={paginatedItems}
      loadingText="Loading resources"
      sortingColumn={{
        sortingField: sortingField,
      }}
      sortingDescending={sortingDescending}
      sortingDisabled={false}
      onSortingChange={({ detail }) => {
        setSortingField(detail.sortingColumn.sortingField as SortingField);
        setSortingDescending(detail.isDescending ?? false);
        setCurrentPage(1); // Reset to first page when sorting changes
      }}
      ariaLabels={{
        selectionGroupLabel: "Services selection",
        allItemsSelectionLabel: () => "select all",
        itemSelectionLabel: ({ selectedItems }, item) => item.serviceCode,
      }}
      filter={
        <TextFilter
          filteringText={filteringText}
          filteringPlaceholder="Find services"
          filteringAriaLabel="Filter services"
          onChange={({ detail }) => {
            setFilteringText(detail.filteringText);
            setCurrentPage(1);
          }}
          countText={`${filteredItems.length} matches`}
        />
      }
      empty={
        <Box textAlign="center" color="inherit">
          <b>No data available</b>
          <Box variant="p" color="inherit">
            There are no modern services available
          </Box>
        </Box>
      }
      pagination={
        <Pagination
          currentPageIndex={currentPage}
          onChange={({ detail }) => setCurrentPage(detail.currentPageIndex)}
          pagesCount={pagesCount}
        />
      }
      enableKeyboardNavigation
      trackBy="serviceCode"
      variant="full-page"
      stickyHeader
      wrapLines
    />
  );
};

export default ModernServicesTable;
